import React from 'react';
import { Drawer, Tag, Button as AntButton } from 'antd';
import { Checkbox, Button } from 'components';
import { inputs } from 'lib/predefinedValues/constants';
import { predefinedConstants } from 'lib/predefinedValues/constants';
import { IAiEnrichmentResult, IEnrichedField, TAG_FIELDS } from 'lib/Interfaces/IAiEnrichment';
import {
  DrawerHeader,
  FieldList,
  FieldRow,
  FieldContent,
  FieldLabel,
  FieldValue,
  TagsContainer,
  ConfidenceBadge,
  EmptyState,
  DrawerFooter,
  DuplicateBanner,
} from './styled';

type AiEnrichmentDrawerProps = {
  open: boolean;
  onClose: () => void;
  enrichmentResult: IAiEnrichmentResult | null;
  selectedFields: Set<string>;
  onToggleField: (field: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onApply: () => void;
  selectedLanguage: string;
  duplicateMatches?: Array<{ dataProcessor: string; templateId: string }>;
};

const fieldLabelMap: Record<string, string> = Object.fromEntries(inputs.map((i) => [i.name, i.label]));

const getConfidenceLevel = (confidence?: number): 'high' | 'medium' | 'low' => {
  if (confidence === undefined) return 'high';
  if (confidence >= 0.8) return 'high';
  if (confidence >= 0.5) return 'medium';
  return 'low';
};

const getConfidenceLabel = (confidence?: number): string => {
  if (confidence === undefined) return '';
  return `${Math.round(confidence * 100)}%`;
};

const getTagLabel = (fieldName: string, key: string, language: string): string => {
  const constants = predefinedConstants as Record<string, Record<string, Record<string, string>>>;
  const fieldData = constants[fieldName];
  if (!fieldData) return key;
  const langData = fieldData[language] || fieldData['en'];
  if (!langData) return key;
  return langData[key] || key;
};

const AiEnrichmentDrawer = ({
  open,
  onClose,
  enrichmentResult,
  selectedFields,
  onToggleField,
  onSelectAll,
  onDeselectAll,
  onApply,
  selectedLanguage,
  duplicateMatches = [],
}: AiEnrichmentDrawerProps) => {
  const presentFields = enrichmentResult
    ? (Object.keys(enrichmentResult) as (keyof IAiEnrichmentResult)[]).filter(
        (key) => key !== 'scrapingStatus' && enrichmentResult[key] != null,
      )
    : [];

  const hasAnyField = presentFields.length > 0;

  const footer = (
    <DrawerFooter>
      <Button styleType="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button styleType="primary" disabled={selectedFields.size === 0} onClick={onApply}>
        Apply Selected ({selectedFields.size})
      </Button>
    </DrawerFooter>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="AI Suggestions — review and select fields to apply"
      placement="right"
      width={480}
      zIndex={1001}
      footer={footer}
    >
      <DrawerHeader>
        <AntButton type="link" size="small" onClick={onSelectAll} disabled={!hasAnyField}>
          Select All
        </AntButton>
        <AntButton type="link" size="small" onClick={onDeselectAll} disabled={!hasAnyField}>
          Deselect All
        </AntButton>
      </DrawerHeader>

      {duplicateMatches.length > 0 && (
        <DuplicateBanner>
          <strong>Potential duplicates found</strong>
          The following existing DPS templates may already cover this service:
          <ul>
            {duplicateMatches.map((m) => (
              <li key={m.templateId}>
                {m.dataProcessor} ({m.templateId})
              </li>
            ))}
          </ul>
        </DuplicateBanner>
      )}

      {!hasAnyField ? (
        <EmptyState>No suggestions could be extracted from the provided URL.</EmptyState>
      ) : (
        <FieldList>
          {presentFields.map((fieldName) => {
            const raw = enrichmentResult?.[fieldName];
            if (!raw || typeof raw !== 'object' || !('value' in raw)) return null;
            const suggestion = raw as IEnrichedField;
            const isSelected = selectedFields.has(fieldName);
            const confidenceLevel = getConfidenceLevel(suggestion.confidence);
            const confidenceLabel = getConfidenceLabel(suggestion.confidence);
            const label = fieldLabelMap[fieldName] || fieldName;
            const isTagField = TAG_FIELDS.has(fieldName);

            return (
              <FieldRow key={fieldName} onClick={() => onToggleField(fieldName)} style={{ cursor: 'pointer' }}>
                <Checkbox
                  checked={isSelected}
                  onChange={() => onToggleField(fieldName)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ marginTop: 2 }}
                />
                <FieldContent>
                  <FieldLabel>{label}</FieldLabel>
                  <FieldValue>
                    {isTagField ? (
                      <TagsContainer>
                        {suggestion.value.map((v) => (
                          <Tag key={v}>{getTagLabel(fieldName, v, selectedLanguage)}</Tag>
                        ))}
                      </TagsContainer>
                    ) : (
                      suggestion.value[0] || <em style={{ color: '#bfbfbf' }}>empty</em>
                    )}
                  </FieldValue>
                </FieldContent>
                {confidenceLabel && <ConfidenceBadge level={confidenceLevel}>{confidenceLabel}</ConfidenceBadge>}
              </FieldRow>
            );
          })}
        </FieldList>
      )}
    </Drawer>
  );
};

export default AiEnrichmentDrawer;
